import React from "react";

import { cn } from "@/lib/utils";

import { Input } from "./shadcn/input";
import { Label } from "./shadcn/label";

export interface TextInputFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  label?: string;
  description?: string;
  errorMessage?: string;
  inputClassName?: string;
  placeholder?: string;
  type?: "text" | "email" | "password" | "search" | "url" | "tel";
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  size?: "sm" | "md" | "lg";
  isInvalid?: boolean;
  validationBehavior?: string;
}

export const TextInputField = React.forwardRef<
  HTMLInputElement,
  TextInputFieldProps
>(
  (
    {
      label,
      description,
      errorMessage,
      className,
      inputClassName,
      placeholder,
      type = "text",
      endAdornment,
      startAdornment,
      size = "md",
      isInvalid = false,
      id: idProp,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const id = idProp ?? generatedId;
    const heightClass = { sm: "h-9", md: "h-10", lg: "h-[50px]" }[size];

    const paddingClass = { sm: "px-3", md: "px-3", lg: "px-4" }[size];

    const textClass = { sm: "text-sm", md: "text-sm", lg: "text-[0.95rem]" }[
      size
    ];

    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {label ? (
          <Label
            htmlFor={id}
            className="text-sl-950 text-xs font-medium tracking-wide"
          >
            {label}
          </Label>
        ) : null}
        <div className="relative flex items-center">
          {startAdornment ? (
            <div className="text-sl-400 absolute top-1/2 left-3 -translate-y-1/2">
              {startAdornment}
            </div>
          ) : null}
          <Input
            {...props}
            id={id}
            ref={ref}
            type={type}
            placeholder={placeholder}
            aria-invalid={isInvalid}
            className={cn(
              isInvalid && "border-re-200 focus-visible:ring-re-100",
              heightClass,
              textClass,
              paddingClass,
              startAdornment && "pl-10",
              endAdornment && "pr-10",
              inputClassName,
            )}
          />
          {endAdornment ? (
            <div className="text-sl-400 absolute top-1/2 right-3 -translate-y-1/2">
              {endAdornment}
            </div>
          ) : null}
        </div>
        {description ? (
          <p className="text-sl-500 text-xs font-medium">{description}</p>
        ) : null}
        <p className="text-re-200 text-xs">{errorMessage}</p>
      </div>
    );
  },
);

TextInputField.displayName = "TextInputField";
